import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import { formatInsertRuleAPI } from '@/features/internal/format-insert-rules/apis/formatInsertRule'
import { FormatInsertRule } from '@/features/internal/format-insert-rules/types/formatInsertRule'
import { useToast } from '@/shared/contexts/ToastContext'

/**
 * フォーマット挿入ルール一覧取得クエリ（TanStack Query）
 *
 * 責務:
 * - フォーマット挿入ルール一覧の取得
 * - キャッシュ管理
 *
 * 使用例:
 * ```tsx
 * const { data, isLoading, error, refetch } = useFormatInsertRulesQuery()
 * const rules = data ?? []
 * ```
 */
export const useFormatInsertRulesQuery = () => {
  return useQuery({
    queryKey: queryKeys.formatInsertRules.all,
    queryFn: async (): Promise<FormatInsertRule[]> => {
      const data = await formatInsertRuleAPI.getRules()
      return data
    },
    staleTime: 1000 * 60 * 5, // 5分キャッシュ
  })
}

/**
 * フォーマット挿入ルール作成ミューテーション
 */
export const useCreateFormatInsertRuleMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async (
      rule: Omit<FormatInsertRule, 'id' | 'created_at' | 'updated_at'>
    ): Promise<FormatInsertRule> => {
      return await formatInsertRuleAPI.createRule(rule)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.formatInsertRules.all,
      })
      showToast('ルールを作成しました', 'success')
    },
    onError: () => {
      showToast('ルールの作成に失敗しました', 'error')
    },
  })
}

/**
 * フォーマット挿入ルール更新ミューテーション
 */
export const useUpdateFormatInsertRuleMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async ({
      id,
      rule,
    }: {
      id: string
      rule: Partial<FormatInsertRule>
    }): Promise<FormatInsertRule> => {
      return await formatInsertRuleAPI.updateRule(id, rule)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.formatInsertRules.all,
      })
      showToast('ルールを更新しました', 'success')
    },
    onError: () => {
      showToast('ルールの更新に失敗しました', 'error')
    },
  })
}

/**
 * フォーマット挿入ルール有効/無効切り替えミューテーション
 */
export const useToggleFormatInsertRuleMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      id: string
    ): Promise<{ id: string; is_active: boolean; message: string }> => {
      return await formatInsertRuleAPI.toggleActive(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.formatInsertRules.all,
      })
    },
  })
}

/**
 * フォーマット挿入ルール削除ミューテーション
 */
export const useDeleteFormatInsertRuleMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await formatInsertRuleAPI.deleteRule(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.formatInsertRules.all,
      })
      showToast('ルールを削除しました', 'success')
    },
    onError: () => {
      showToast('ルールの削除に失敗しました', 'error')
    },
  })
}
